import React from "react";
import { COLORS } from "./constants";
import { presetFor, presetLabel } from "./platformPresets";

export const getStorageValue = (key) =>
  typeof window === "undefined" ? "" : window.localStorage.getItem(key) || "";

export const getInitialUserId = (routeId) =>
  getStorageValue("userId") || routeId || "";

export const formatUptime = (secs) => {
  if (!secs || secs < 0) return null;
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

export const buildPlatformList = ({
  youtubeKeys, facebookKeys, tiktokKey, rtmpDestinations, platformConnections, labels
}) => {
  const list = [];
  // Mirrors labelFor() in the backend's platformDestinationService so the name
  // shown in the dashboard is the same one used for the destination itself.
  const labelFor = (id, fallback) => {
    const value = labels?.[id];
    return (typeof value === "string" && value.trim()) ? value.trim() : fallback;
  };

  youtubeKeys.forEach((_, i) =>
    list.push({ id: `YouTube${i + 1}`, label: labelFor(`YouTube${i + 1}`, `YouTube ${i + 1}`), kind: "youtube" })
  );
  // OAuth-connected accounts are a separate destination from manual keys, and
  // the backend (platformDestinationService.buildAllLiveDestinations) only
  // includes them when enabled — so mirror that here rather than always
  // showing a connected account that won't actually receive the stream.
  // The id MUST match what fffmpegService/streamStatus key their live state
  // by ("YouTube-Connected" / "Facebook-Connected"), or status/stop/restart
  // have nothing to attach to.
  if (platformConnections?.youtube?.enabled) {
    list.push({
      id: "YouTube-Connected",
      label: platformConnections.youtube.selectedName || platformConnections.youtube.channelTitle || "YouTube (Connected)",
      kind: "youtube"
    });
  }
  facebookKeys.forEach((_, i) =>
    list.push({ id: `Facebook${i + 1}`, label: labelFor(`Facebook${i + 1}`, `Facebook ${i + 1}`), kind: "facebook" })
  );
  if (platformConnections?.facebook?.enabled) {
    list.push({
      id: "Facebook-Connected",
      label: platformConnections.facebook.selectedName || platformConnections.facebook.pageName || "Facebook (Connected)",
      kind: "facebook"
    });
  }
  if (tiktokKey) list.push({ id: "TikTok", label: labelFor("TikTok", "TikTok"), kind: "tiktok" });
  // Custom RTMP and the preset platforms (LinkedIn, X, Instagram, Kick) share
  // one array, because all of them are url + key rather than key-only.
  //
  // The ids MUST be counted per platform, exactly as buildManualDestinations
  // does on the server — they are what stream status and stop/restart key off,
  // and an id that disagrees with the server's leaves a destination the
  // dashboard can see but cannot control.
  const seenPerPlatform = {};
  rtmpDestinations.forEach((d) => {
    const preset  = presetFor(d?.platform);
    const idBase  = preset ? preset.label.replace(/[^A-Za-z0-9]/g, "") : "CustomRTMP";
    const ordinal = (seenPerPlatform[idBase] = (seenPerPlatform[idBase] || 0) + 1);
    const id      = `${idBase}${ordinal}`;
    list.push({
      id,
      label: (typeof d?.name === "string" && d.name.trim())
        ? d.name.trim()
        : labelFor(id, presetLabel(preset, ordinal)),
      kind: preset ? preset.kind : "rtmp"
    });
  });
  // The SRT relay used to be appended here as a permanent row. It is a SOURCE
  // (camera -> server -> player), not an output, so it inflated every count
  // ("2 destinations", "1 of 2 sending" with one real output). Its controls
  // live in the SRT tab of the Source card.
  return list;
};

// Platform name shown before the user's own label, so a row reads
// "YouTube · test" rather than just "test".
const KIND_NAMES = {
  youtube: "YouTube", facebook: "Facebook", tiktok: "TikTok", linkedin: "LinkedIn",
  x: "X", instagram: "Instagram", kick: "Kick", rtmp: "Custom RTMP"
};
export const platformName = (kind) => KIND_NAMES[kind] || "RTMP";

// ── Platform glyphs ──────────────────────────────────────────────────────────
//
// One set, all drawn the same way: a rounded tile in the platform's colour
// with a simple mark on it (from the design canvas UI kit). The app used to
// mix MUI icons of different weights and sizes, with a game controller
// standing in for Kick and an HDMI plug for custom RTMP.
const GLYPHS = {
  youtube:   { tile: "#EF4444", fill: <path d="M10 8.5v7l6-3.5z" fill="#fff" /> },
  facebook:  { tile: "#1877F2", fill: <path fill="#fff" d="M13.2 20v-6.3h2.1l.3-2.5h-2.4V9.6c0-.7.2-1.2 1.2-1.2h1.3V6.2a17 17 0 0 0-1.9-.1c-1.9 0-3.2 1.2-3.2 3.3v1.8H8.5v2.5h2.1V20z" /> },
  tiktok:    { tile: "#111111", stroke: "#fff", d: "M13 6v8.5a2.5 2.5 0 1 1-2.5-2.5M13 6c.6 1.9 2 3 4 3.1" },
  linkedin:  { tile: "#0A66C2", stroke: "#fff", d: "M7.5 10.5v6M7.5 7.5v.01M11 16.5v-6M11 13a2.5 2.5 0 0 1 5 0v3.5" },
  x:         { tile: "#000000", edge: "rgba(239,227,204,0.25)", stroke: "#fff", d: "M7.5 7l9 10M16.5 7l-9 10" },
  instagram: { tile: "#E1306C", stroke: "#fff", extra: (
    <>
      <rect x="6.5" y="6.5" width="11" height="11" rx="3.5" />
      <circle cx="12" cy="12" r="2.6" />
    </>
  ) },
  kick:      { tile: "#53FC18", stroke: "#0B0B0B", width: 2.4, d: "M8.5 6.5v11M8.5 12l6.5-5.5M8.5 12l6.5 5.5" },
  srt:       { tile: "#2A2760", stroke: "#3DD68C", d: "M5 12h3l2-4 4 8 2-4h3" },
  rtmp:      { tile: "#2A2760", stroke: "#2BBDAF", d: "M6 8h12M6 12h12M6 16h7" }
};

export function PlatformGlyph({ kind, size = 20, title }) {
  const g = GLYPHS[kind] || GLYPHS.rtmp;
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} role={title ? "img" : undefined}
      aria-hidden={title ? undefined : true} style={{ display: "block", flex: "none" }}>
      {title && <title>{title}</title>}
      <rect x="1" y="1" width="22" height="22" rx="6" fill={g.tile}
        stroke={g.edge || "none"} strokeWidth={g.edge ? 1 : 0} />
      {g.fill}
      {(g.d || g.extra) && (
        <g fill="none" stroke={g.stroke} strokeWidth={g.width || 2} strokeLinecap="round" strokeLinejoin="round">
          {g.d && <path d={g.d} />}
          {g.extra}
        </g>
      )}
    </svg>
  );
}

// Kept as a function for the existing call sites.
export const platformIcon = (kind, size = 20) => <PlatformGlyph kind={kind} size={size} />;

export const formatBytes = (b) => {
  if (!b) return "–";
  if (b >= 1e9) return `${(b / 1e9).toFixed(2)} GB`;
  if (b >= 1e6) return `${(b / 1e6).toFixed(0)} MB`;
  return `${(b / 1e3).toFixed(0)} KB`;
};

// ── Masked secrets ───────────────────────────────────────────────────────────
//
// The backend never sends a stream key back in full. It sends a mask such as
// "••••••••mnop", and accepts that
// same mask on save to mean "leave this key as it is". The bullet cannot occur
// in a real stream key, which is what makes this test reliable.
//
// Must stay in step with MASK_CHAR in backend-/utils/secretBox.js.
export const MASK_CHAR = "•";

export const isMaskedValue = (value) =>
  typeof value === "string" && value.includes(MASK_CHAR);

// Spread onto any input bound to a possibly-masked secret. Selecting the whole
// mask on focus means the first keystroke replaces all of it, so the user cannot
// accidentally save a half-edited mask — the server would reject that with a
// 409 rather than guess which key was meant.
export const maskedInputProps = (value) => ({
  onFocus: (event) => { if (isMaskedValue(value)) event.target.select(); },
  helperText: isMaskedValue(value) ? "Saved and hidden — type to replace" : undefined,
  // MUI's default helper colour is a light grey meant for light backgrounds.
  FormHelperTextProps: { sx: { color: COLORS.muted, fontSize: 10, ml: 0.5, mt: 0.25 } }
});
